import { useState, useEffect } from 'react'
import { Link } from '@tanstack/react-router'
import { useReactTable, getCoreRowModel, flexRender, type ColumnDef } from '@tanstack/react-table'
import { Plus, Search } from 'lucide-react'

import { useCustomers, useCreateCustomer } from '@/features/customers/hooks'
import { CUSTOMER_STATUS_LABELS, CUSTOMER_STATUS_VARIANT, customerDisplayName } from '@/features/customers/customer-utils'
import { useDebounce } from '@/components/hooks/use-debounce'
import { CustomerForm, type CustomerFormValues } from '@/features/customers/customer-form'
import { useDirtyClose } from '@/hooks/use-dirty-close'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Customer } from '@/types/customers'

interface CustomersTabProps {
  q?: string
  onQChange?: (q: string) => void
}

// Stable identity for the loading state: TanStack Table reruns its row models
// (and resets pagination state) whenever `data` changes identity, so a fresh
// `[]` on every render would loop forever while the query is in flight.
const EMPTY_ROWS: Customer[] = []

const columns: ColumnDef<Customer>[] = [
  {
    id: 'name',
    header: 'Name',
    cell: ({ row }) => (
      <div className="font-medium">
        {customerDisplayName(row.original)}
      </div>
    ),
  },
  {
    accessorKey: 'company_name',
    header: 'Company',
    cell: ({ row }) => row.original.company_name || '—',
  },
  {
    accessorKey: 'contact_email',
    header: 'Email',
    cell: ({ row }) => row.original.contact_email || '—',
  },
  {
    accessorKey: 'phone',
    header: 'Phone',
    cell: ({ row }) => row.original.phone || '—',
  },
  {
    accessorKey: 'assigned_sales_rep',
    header: 'Sales Rep',
    cell: ({ row }) => row.original.assigned_sales_rep || '—',
  },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) => (
      <Badge variant={CUSTOMER_STATUS_VARIANT[row.original.status]}>
        {CUSTOMER_STATUS_LABELS[row.original.status]}
      </Badge>
    ),
  },
  {
    id: 'actions',
    cell: ({ row }) => (
      <Link to="/sales/$customerId" params={{ customerId: row.original.id }}>
        <Button variant="outline" size="sm">
          View
        </Button>
      </Link>
    ),
  },
]

export function CustomersTab({ q: urlQ, onQChange }: CustomersTabProps) {
  const [searchInput, setSearchInput] = useState(urlQ ?? '')
  const debouncedQ = useDebounce(searchInput, 300)

  useEffect(() => {
    if (onQChange && debouncedQ !== (urlQ ?? '')) {
      onQChange(debouncedQ)
    }
  }, [debouncedQ, urlQ, onQChange])

  const { data, isLoading, isError } = useCustomers(debouncedQ || undefined)
  const createMutation = useCreateCustomer()
  const [createOpen, setCreateOpen] = useState(false)
  const [formDirty, setFormDirty] = useState(false)

  const { guardOpenChange: guardCreateOpen, discardGuard: createDirtyGuard } = useDirtyClose(
    createOpen,
    setCreateOpen,
    formDirty,
  )

  const handleCreate = (values: CustomerFormValues) => {
    createMutation.mutate(
      { ...values, phone: values.phone ?? '', company_name: values.company_name ?? '' },
      {
        onSuccess: () => setCreateOpen(false),
      },
    )
  }

  // Search is always server-side: the backend matches name, company, email,
  // and phone (including digit-normalized phone) on the debounced query.
  const table = useReactTable({
    data: data ?? EMPTY_ROWS,
    columns,
    getCoreRowModel: getCoreRowModel(),
  })

  if (isLoading) {
    return (
      <div className="flex flex-col gap-4">
        <div className="flex justify-end">
          <Skeleton className="h-10 w-40" />
        </div>
        <Skeleton className="h-10 w-full max-w-sm" />
        <Skeleton className="h-[400px] w-full" />
      </div>
    )
  }

  if (isError) {
    return <div className="text-destructive">Failed to load customers.</div>
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative w-full sm:max-w-sm">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search name, phone, email, company…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="pl-8"
          />
        </div>
        <Dialog open={createOpen} onOpenChange={guardCreateOpen}>
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="mr-2 h-4 w-4" /> New Customer
          </Button>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>New Customer</DialogTitle>
              <DialogDescription>Add a new customer.</DialogDescription>
            </DialogHeader>
            <CustomerForm
              onDirtyChange={setFormDirty}
              onSubmit={handleCreate}
              isPending={createMutation.isPending}
              submitLabel="Create Customer"
            />
          </DialogContent>
                {createDirtyGuard}
</Dialog>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  No customers found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}
